import { Capacitor } from '@capacitor/core';
import { pushApi } from './pushApi';
import { useUIStore, type ScreenName } from '@/stores/uiStore';

// ─── Push notifications (native only) ────────────────────────────────────────
// Uses @capacitor-firebase/messaging so BOTH platforms hand us a real FCM
// registration token (the plain @capacitor/push-notifications plugin returns a
// raw APNs token on iOS, which firebase-admin can't deliver to). On native we
// ask permission once, register the FCM token with the server, and deep-link
// the app when a notification is tapped (payload `data.screen`). Web push (PWA)
// is a separate phase; on web this whole module no-ops.

let started = false;
let lastToken: string | null = null;

const VALID_SCREENS: ReadonlySet<string> = new Set<ScreenName>([
  'home',
  'leaderboard',
  'shop',
  'inventory',
  'friends',
  'settings',
]);

/** Navigate when a notification is tapped. Falls back to home. */
function handleTap(data: Record<string, unknown> | undefined): void {
  const screen =
    data && typeof data.screen === 'string' && VALID_SCREENS.has(data.screen)
      ? (data.screen as ScreenName)
      : 'home';
  try {
    useUIStore.getState().go(screen);
  } catch {
    /* store not ready — ignore */
  }
}

/** The device's UI language as a BCP-47 tag (e.g. "th-TH"); server normalizes
 * it to a supported locale for per-language push targeting. */
function deviceLocale(): string {
  try {
    return navigator.language || 'en';
  } catch {
    return 'en';
  }
}

/** Register the FCM token with the server (de-duped against the last value). */
function syncToken(token: string | null | undefined): void {
  if (!token || token === lastToken) return;
  lastToken = token;
  void pushApi.register(token, Capacitor.getPlatform(), deviceLocale()).catch(() => {});
}

/** Ask permission (once), register the device token, wire tap handling. Safe to
 * call on every successful login — guarded so it only runs once. */
export async function initPush(): Promise<void> {
  if (started) return;
  if (Capacitor.getPlatform() === 'web') return; // web push = Phase 3
  started = true;
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await FirebaseMessaging.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      started = false; // let a later login re-prompt if the user changes their mind
      return;
    }

    // FCM tokens can rotate at any time — keep the server in sync on refresh.
    await FirebaseMessaging.addListener('tokenReceived', (event) => {
      syncToken(event.token);
    });
    // Tap on a (background/closed) notification → deep-link to the screen.
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      handleTap(event.notification?.data as Record<string, unknown> | undefined);
    });

    // Initial fetch — on iOS this also registers with APNs under the hood and
    // bridges to FCM, so we always get an FCM (not APNs) token.
    const { token } = await FirebaseMessaging.getToken();
    syncToken(token);
  } catch (e) {
    console.warn('[push] init failed', e);
    started = false;
  }
}

/** Drop the token + listeners on logout so the next user starts clean. */
export async function teardownPush(): Promise<void> {
  if (Capacitor.getPlatform() === 'web') return;
  try {
    if (lastToken) await pushApi.unregister(lastToken).catch(() => {});
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    await FirebaseMessaging.deleteToken().catch(() => {});
    await FirebaseMessaging.removeAllListeners();
  } catch {
    /* ignore */
  }
  started = false;
  lastToken = null;
}
